import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const testimonials = [
  {
    name: '林清月',
    role: '文化评论人',
    content: '爱风月让我重新发现了生活中的诗意。在这里，风月不再是遥远的古典意象，而是触手可及的日常之美。每一篇文章都像一盏灯，照亮了我对生活的感知。',
  },
  {
    name: '苏晚风',
    role: '独立设计师',
    content: '作为一个追求东方美学的设计师，爱风月是我的灵感源泉。它将传统文化用现代的方式重新诠释，让我在设计工作中获得了源源不断的启发。',
  },
  {
    name: '陈雅集',
    role: '资深读者',
    content: '关注爱风月已经两年了，每期内容都不曾错过。它不只是传递知识，更是在传递一种生活态度——在平凡中发现诗意，在日常中体味风雅。',
  },
];

const values = [
  { icon: 'ri-moon-clear-line', title: '诗意栖居', desc: '在喧嚣都市中，为心灵留一方净土，让生活回归本真的诗意与宁静。' },
  { icon: 'ri-heart-pulse-line', title: '雅致格调', desc: '追求精致而不浮华，讲究格调而不做作，让每一个日常都充满美学质感。' },
  { icon: 'ri-book-open-line', title: '文化传承', desc: '将千年文脉中的审美智慧，以现代方式传递给更多人，让古典之美在当代延续。' },
  { icon: 'ri-community-line', title: '同好共聚', desc: '汇聚热爱风月之人，在分享与交流中共同成长，让风雅成为一种生活方式。' },
];

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = fd.get('company_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('提交成功！我们会尽快与您联系。');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    const params = new URLSearchParams();
    params.append('name', formData.name);
    params.append('email', formData.email);
    params.append('message', formData.message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9snm0dlbrt1qcgaj6k0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！我们会尽快与您联系。');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
        if (serverMsg.toLowerCase().includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络异常，请稍后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于爱风月',
        description: '爱风月品牌介绍、核心价值与联系方式。汇聚风月文化，传递诗意生活方式。',
        url: 'https://aifengyue.com/about',
        mainEntity: {
          '@type': 'Organization',
          name: '爱风月',
          description: '爱风月，是一种对生活诗意与浪漫的深情追求。汇聚风月文化内涵、生活方式与美学体验。',
          url: 'https://aifengyue.com',
          email: 'hello@aifengyue.com',
          telephone: '010-8888-6666',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '南锣鼓巷风月胡同8号',
            addressLocality: '北京',
            addressRegion: '东城区',
            addressCountry: 'CN',
          },
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aifengyue.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '爱风月是一个什么样的品牌？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '爱风月是一个专注于风雅生活美学的文化品牌，致力于传播风月文化内涵、分享诗意浪漫生活方式。',
            },
          },
          {
            '@type': 'Question',
            name: '如何参加爱风月的线下体验活动？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '我们的线下体验活动会定期在北京举办，活动信息会提前通过通讯邮件和公众号发布。',
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title="关于我们 - 爱风月 | 风雅生活美学品牌"
        description="了解爱风月品牌故事、核心价值与联系方式。爱风月致力于风月文化传播与生活美学推广，让诗意浪漫成为每个人的日常。"
        keywords="爱风月,关于我们,品牌介绍,风月文化,风雅生活美学,联系方式"
        canonicalUrl="https://aifengyue.com/about"
        schema={aboutSchema}
      />

      <Navbar />

      <main>
        <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <div className="mt-8 text-center">
              <p className="text-primary-500 font-heading text-sm tracking-[0.2em] mb-3">关于爱风月</p>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground-900 mb-4">
                以风为骨，以月为魂
              </h1>
              <p className="text-foreground-500 text-base leading-relaxed max-w-3xl mx-auto">
                爱风月，是一个专注于风雅生活美学的文化品牌。我们相信，生活中的诗意与浪漫不是远方的风景，
                而是存在于每一个日常瞬间。从茶道到花艺，从诗词到古乐，我们致力于发掘并传播风月文化的内涵与外延，
                让更多人感受到那份独属于东方的雅致与情韵。
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  我们的故事
                </h2>
                <div className="space-y-4 text-foreground-600 text-sm leading-relaxed">
                  <p>
                    爱风月创立于北京，根植于千年文脉，面向当代生活。我们的创始团队由一群热爱中国传统文化、
                    追求生活美学的年轻人组成。在快节奏的现代都市中，我们希望能为每一个疲惫的灵魂提供一处诗意栖居的精神家园。
                  </p>
                  <p>
                    "风月"二字，在中华文化中承载了太多的美好意蕴——它是李白月下独酌的洒脱，是苏轼把酒问青天的豪情，
                    是张若虚春江花月夜的宇宙意识，也是无数普通人在平凡生活中对诗意的不懈追求。
                  </p>
                  <p>
                    我们将这份跨越千年的审美精神，转化为当代人可以理解、可以触摸、可以实践的生活方式。
                    在爱风月，你不仅能读到关于风月文化的深度文章，更能在我们的体验活动中亲身感受那份雅致与美好。
                  </p>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Traditional%20Chinese%20study%20room%20with%20wooden%20desk%2C%20ink%20brush%20calligraphy%2C%20tea%20set%2C%20antique%20bookshelves%2C%20warm%20afternoon%20sunlight%20through%20paper%20windows%2C%20scholarly%20atmosphere%2C%20elegant%20cultural%20space%2C%20peaceful%20and%20inspiring%20interior%2C%20soft%20natural%20tones&width=800&height=600&seq=about-story&orientation=landscape"
                  alt="爱风月品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <p className="text-accent-600 font-heading text-sm tracking-[0.2em] mb-3">核心价值</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                我们坚守的信念
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center hover:border-accent-200/50 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-50 text-accent-500 mb-4 group-hover:bg-accent-500 group-hover:text-white transition-all duration-300">
                    <i className={`${v.icon} text-xl`} />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <p className="text-primary-500 font-heading text-sm tracking-[0.2em] mb-3">口碑好评</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                读者心目中的爱风月
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8"
                >
                  <div className="flex items-center gap-1 text-accent-500 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm" />
                    ))}
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-6 italic">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-heading text-sm font-bold">
                      {t.name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                      <i className="ri-map-pin-line" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">地址</p>
                      <p className="text-sm text-foreground-500">北京市东城区南锣鼓巷风月胡同8号</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                      <i className="ri-mail-line" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">邮箱</p>
                      <p className="text-sm text-foreground-500">hello@aifengyue.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 shrink-0">
                      <i className="ri-phone-line" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电话</p>
                      <p className="text-sm text-foreground-500">010-8888-6666</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg overflow-hidden h-48">
                  <iframe
                    title="爱风月地址"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12221.452680844!2d116.403963!3d39.937534!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f052d44e5c79f9%3A0x8efd4b7e2b8e3f4e!2sNanluoguxiang!5e0!3m2!1szh-CN!2scn!4v1690000000000!5m2!1szh-CN!2scn"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  发送消息
                </h2>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-5"
                >
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="wf-required"
                  />
                  <div>
                    <label htmlFor="ab-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="ab-name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="ab-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="ab-email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="ab-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      id="ab-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    />
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    className="whitespace-nowrap w-full py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-300 text-sm"
                  >
                    发送消息
                  </button>

                  {formStatus !== 'idle' && (
                    <p className={`text-sm text-center ${formStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                      {formMessage}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}